/**
 * Zoom Recording Selector Component
 * 
 * Displays list of user's Zoom recordings with filters and import functionality.
 * Used in the presentation upload wizard.
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    TextField,
    Alert,
    CircularProgress,
    Grid,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
} from '@mui/material';
import {
    VideoCall as VideoIcon,
    Refresh,
    Download,
    CalendarToday,
    Schedule,
    Storage,
    Search
} from '@mui/icons-material';
import { fetchZoomRecordings, importZoomRecording, getZoomConnectionStatus } from '../../utils/api/zoomApi';

interface ZoomRecording {
    id: string;
    topic: string;
    startTime: string;
    duration: number;
    fileSize: number;
    [key: string]: any;
}

interface ZoomRecordingSelectorProps {
    onRecordingSelected?: (recording: ZoomRecording) => void;
    onImportComplete?: (presentationId: string, recording: ZoomRecording) => void;
}

const ZoomRecordingSelector: React.FC<ZoomRecordingSelectorProps> = ({ onRecordingSelected, onImportComplete }) => {
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [recordings, setRecordings] = useState<ZoomRecording[]>([]);
    const [filteredRecordings, setFilteredRecordings] = useState<ZoomRecording[]>([]);
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedRecording, setSelectedRecording] = useState<ZoomRecording | null>(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [nextPageToken, setNextPageToken] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(false);

    useEffect(() => {
        checkConnection();
    }, []);

    useEffect(() => {
        if (connected) {
            loadRecordings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connected, fromDate, toDate]);

    useEffect(() => {
        filterRecordings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordings, searchTerm]);

    const checkConnection = async () => {
        try {
            const status = await getZoomConnectionStatus();
            setConnected(status.connected || false);
            if (!status.connected) {
                setError('Zoom integration is not connected. Please contact your tenant administrator to connect a Zoom account.');
            }
        } catch (err) {
            console.error('Error checking Zoom connection:', err);
            setError('Failed to check Zoom connection status');
        }
    };

    const loadRecordings = async () => {
        try {
            setLoading(true);
            setError('');

            const result = await fetchZoomRecordings({
                fromDate: fromDate || undefined,
                toDate: toDate || undefined,
                pageSize: 30
            });

            if (!result.success) {
                throw new Error(result.error || 'Failed to fetch recordings');
            }

            setRecordings(result.recordings || []);
            setNextPageToken(result.nextPageToken || null);
            setHasMore(!!result.nextPageToken);
        } catch (err: any) {
            console.error('Error loading recordings:', err);
            setError(err.message || 'Failed to load Zoom recordings');
        } finally {
            setLoading(false);
        }
    };

    const loadMoreRecordings = async () => {
        if (!nextPageToken || loading) return;

        try {
            setLoading(true);
            const result = await fetchZoomRecordings({
                fromDate: fromDate || undefined,
                toDate: toDate || undefined,
                pageSize: 30,
                nextPageToken
            });

            if (result.success) {
                setRecordings(prev => [...prev, ...(result.recordings || [])]);
                setNextPageToken(result.nextPageToken || null);
                setHasMore(!!result.nextPageToken);
            }
        } catch (err: any) {
            console.error('Error loading more recordings:', err);
            setError(err.message || 'Failed to load more recordings');
        } finally {
            setLoading(false);
        }
    };

    const filterRecordings = () => {
        if (!searchTerm) {
            setFilteredRecordings(recordings);
            return;
        }

        const term = searchTerm.toLowerCase();
        const filtered = recordings.filter(recording =>
            recording.topic.toLowerCase().includes(term)
        );
        setFilteredRecordings(filtered);
    };

    const handleImport = async (recording: ZoomRecording) => {
        setSelectedRecording(recording);
        if (onRecordingSelected) {
            onRecordingSelected(recording);
        }
        setConfirmDialogOpen(true);
    };

    const confirmImport = async () => {
        if (!selectedRecording) return;

        try {
            setImporting(true);
            setError('');
            setConfirmDialogOpen(false);

            const result = await importZoomRecording(selectedRecording);

            if (!result.success) {
                throw new Error(result.error || 'Failed to import recording');
            }

            if (onImportComplete) {
                onImportComplete(result.presentationId, selectedRecording);
            }

            // Remove imported recording from list
            setRecordings(prev => prev.filter(r => r.id !== selectedRecording.id));
            setFilteredRecordings(prev => prev.filter(r => r.id !== selectedRecording.id));
        } catch (err: any) {
            console.error('Error importing recording:', err);
            setError(err.message || 'Failed to import recording');
        } finally {
            setImporting(false);
            setSelectedRecording(null);
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'Unknown date';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatDuration = (seconds: number) => {
        if (!seconds) return 'Unknown';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formatFileSize = (bytes: number) => {
        if (!bytes) return 'Unknown size';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
    };

    if (!connected) {
        return (
            <Alert severity="info">
                <Typography variant="body2">
                    Zoom integration is not connected. Please contact your tenant administrator to connect a Zoom account.
                </Typography>
            </Alert>
        );
    }

    return (
        <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Select Zoom Recording</Typography>
                <Button
                    startIcon={<Refresh />}
                    onClick={loadRecordings}
                    disabled={loading}
                    size="small"
                >
                    Refresh
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Filters */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                label="Search recordings"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                                }}
                                size="small"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                type="date"
                                label="From Date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                type="date"
                                label="To Date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Recordings List */}
            {loading && recordings.length === 0 ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : filteredRecordings.length === 0 ? (
                <Alert severity="info">
                    {recordings.length === 0
                        ? 'No recordings found. Recordings may take a few minutes to appear after a meeting ends.'
                        : 'No recordings match your search criteria.'}
                </Alert>
            ) : (
                <>
                    <Grid container spacing={2}>
                        {filteredRecordings.map((recording, index) => (
                            <Grid key={recording.id} size={{ xs: 12, md: 6 }}>
                                <ListItem sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 1 }}>
                                    <VideoIcon sx={{ mr: 2, color: 'primary.main' }} />
                                    <ListItemText
                                        primary={recording.topic || 'Untitled Meeting'}
                                        secondary={
                                            <Box>
                                                <Chip
                                                    icon={<CalendarToday />}
                                                    label={formatDate(recording.startTime)}
                                                    size="small"
                                                    sx={{ mr: 1 }}
                                                />
                                                <Chip
                                                    icon={<Schedule />}
                                                    label={formatDuration(recording.duration)}
                                                    size="small"
                                                    sx={{ mr: 1 }}
                                                />
                                                <Chip
                                                    icon={<Storage />}
                                                    label={formatFileSize(recording.fileSize)}
                                                    size="small"
                                                />
                                            </Box>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <Button
                                            variant="contained"
                                            startIcon={importing && selectedRecording?.id === recording.id ? <CircularProgress size={16} /> : <Download />}
                                            onClick={() => handleImport(recording)}
                                            disabled={importing}
                                        >
                                            Import
                                        </Button>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            </Grid>
                        ))}
                    </Grid>

                    {hasMore && (
                        <Box display="flex" justifyContent="center" mt={2}>
                            <Button onClick={loadMoreRecordings} disabled={loading}>
                                Load More
                            </Button>
                        </Box>
                    )}
                </>
            )}

            {/* Import Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>Import Zoom Recording</DialogTitle>
                <DialogContent>
                    <Typography>
                        Import "{selectedRecording?.topic || 'this recording'}" into EvalPro2?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        The recording will be downloaded and a new presentation will be created.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={confirmImport}
                        disabled={importing}
                        startIcon={importing ? <CircularProgress size={16} /> : <Download />}
                    >
                        {importing ? 'Importing...' : 'Import'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ZoomRecordingSelector;
